import React from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTheme } from '@mui/material/styles';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { CollapsibleSidebar } from './CollapsibleSidebar';
import { TopBar } from './TopBar';
import { buildBreadcrumbs } from './TopBar';

export interface AppShellProps {
  alertCount?: number;
}

export const AppShell: React.FC<AppShellProps> = ({ alertCount }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const theme = useTheme();
  const location = useLocation();
  const breadcrumbs = buildBreadcrumbs(location.pathname);


  return (
    <Box sx={{ display: 'flex' }}>
      {/* Fixed AppBar */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ bgcolor: 'background.default', zIndex: theme.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ p: 2 }}>
          <TopBar
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
            onMobileMenuToggle={() => setMobileOpen(true)}
          />
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <CollapsibleSidebar
        alertCount={alertCount}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onMobileOpen={() => setMobileOpen(true)}
        collapsed={collapsed}
        onToggle={(val) => setCollapsed(val)}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: '1%',
          width: '100%',
          minHeight: 'calc(100vh - 88px)',
          padding: '20px',
          marginTop: '88px',
          marginRight: '20px',
          borderRadius: '12px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.background.paper,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen + 200,
          }),
          ...(collapsed && {
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen + 200,
            }),
          }),
          [theme.breakpoints.up('md')]: {
            marginLeft: 0,
          },
          [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            padding: '16px',
          },
          [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            marginRight: '10px',
          },
        }}
      >
        {/* Breadcrumbs in main content area */}
        {breadcrumbs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1;
                const isCurrent = !item.path;

                if (isCurrent || isLast) {
                  return (
                    <Typography
                      key={index}
                      color="text.primary"
                      variant="body2"
                      fontWeight={500}
                      aria-current="page"
                    >
                      {item.label}
                    </Typography>
                  );
                }

                return (
                  <Link
                    key={index}
                    component={RouterLink}
                    to={item.path!}
                    underline="hover"
                    color="text.secondary"
                    variant="body2"
                  >
                    {item.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}

        <Outlet />
      </Box>
    </Box>
  );
};

export default AppShell;
